import { IconClose } from '@components/Icon';
import { Link } from '@components/Link';
import useImpersonate from '@State/Impersonate/useImpersonate';
import { ButtonReset } from 'src/layout/reset';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

const DISMISS_KEY = 'maintenance-banner-dismissed';

const Wrapper = styled.section<{ $impersonate?: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  position: fixed;
  top: ${({ $impersonate, theme }) =>
    $impersonate ? `calc(${theme.size.impersonateBarHeight} + 21.5px)` : '21.5px'};
  left: calc(
    ${(props) => props.theme.size.sideBarWidth} +
      max(env(safe-area-inset-left), ${(props) => props.theme.size.containerGutter})
  );
  right: ${(props) => props.theme.size.containerGutter};
  z-index: ${(props) => props.theme.zindex.stateBar};
  padding: 16px 20px;
  background: #fff9e6;
  border: 1px solid #e8d4a0;
  border-radius: 12px;
  color: #1a1a1a;
  box-sizing: border-box;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  ${(props) => props.theme.breakpoint.LtSm} {
    top: ${({ $impersonate, theme }) =>
      $impersonate ? `calc(${theme.size.impersonateBarHeightMobile} + 4.5rem)` : '4.5rem'};
    left: ${(props) => props.theme.size.containerGutterLtSm};
    right: ${(props) => props.theme.size.containerGutterLtSm};
  }
`;

const IconCircle = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #f5e6c0;
  color: #c48a1a;
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

const Title = styled.p`
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.3;
  color: #1a1a1a;
`;

const Description = styled.p`
  margin: 0;
  font-size: 0.9375rem;
  font-weight: 400;
  line-height: 1.4;
  color: #1a1a1a;
`;

const CloseButton = styled(ButtonReset)`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #1a1a1a;
  align-self: flex-start;

  &:hover {
    background: rgba(0, 0, 0, 0.06);
  }
`;

const ToolsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MaintenanceBanner = () => {
  const { t } = useTranslation();
  const { impersonate } = useImpersonate();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === 'true');

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, 'true');
    setDismissed(true);
  };

  if (dismissed) {
    return null;
  }

  return (
    <Wrapper role="status" $impersonate={!!impersonate}>
      <IconCircle>
        <ToolsIcon />
      </IconCircle>
      <TextWrapper>
        <Title>{t('maintenance_banner_title')}</Title>
        <Description>
          <Trans
            i18nKey="maintenance_banner_description"
            components={{
              Link: (
                <Link
                  href={`mailto:${t('remondis_contact_email')}`}
                  title={t('home_contact_link_title')}
                />
              ),
            }}
          />
        </Description>
      </TextWrapper>
      <CloseButton
        type="button"
        onClick={handleDismiss}
        aria-label={t('maintenance_banner_close_btn_title')}
        title={t('maintenance_banner_close_btn_title')}
      >
        <IconClose width={14} height={14} />
      </CloseButton>
    </Wrapper>
  );
};

export default MaintenanceBanner;
